import { LogViewer } from '@backstage/core-components';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { makeStyles } from '@material-ui/core/styles';
import {
  BatchJobWorkflowItem,
  BatchJobWorkflowStep,
  BatchJobWorkflowStepStatus,
  WORKFLOW_STATE_LABELS,
  buildWorkflowLog,
  buildWorkflowSteps,
  formatBatchUserRef,
} from './batchJobWorkflow';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 320px) 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  stepper: {
    padding: theme.spacing(1, 0),
  },
  logPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: 180,
    overflow: 'hidden',
  },
}));

function stepIcon(status: BatchJobWorkflowStepStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircleIcon color="primary" fontSize="small" />;
    case 'running':
      return <HourglassEmptyIcon color="primary" fontSize="small" />;
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />;
    case 'skipped':
      return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
  }
}

function WorkflowStepIcon({ step }: { step: BatchJobWorkflowStep }) {
  return stepIcon(step.status);
}

type BatchJobDetailViewProps = {
  job: BatchJobWorkflowItem;
};

export function BatchJobDetailView({ job }: BatchJobDetailViewProps) {
  const classes = useStyles();
  const steps = buildWorkflowSteps(job);
  const logText = buildWorkflowLog(job);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {job.title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        State: {WORKFLOW_STATE_LABELS[job.workflowState]} · Created by{' '}
        {formatBatchUserRef(job.createdBy)}
        {job.approvedBy ? ` · Approved by ${formatBatchUserRef(job.approvedBy)}` : ''}
        {job.mergedBy ? ` · Merged by ${formatBatchUserRef(job.mergedBy)}` : ''}
      </Typography>

      <Box className={classes.root} mt={2}>
        <Stepper activeStep={-1} orientation="vertical" className={classes.stepper}>
          {steps.map(step => (
            <Step key={step.id} expanded>
              <StepLabel StepIconComponent={() => <WorkflowStepIcon step={step} />}>
                <Typography variant="body2">{step.label}</Typography>
                {step.log ? (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {step.log}
                  </Typography>
                ) : null}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Workflow log
          </Typography>
          <Box className={classes.logPanel}>
            <LogViewer text={logText} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
